import { useEffect } from 'react'

import { useAuthentication } from '@procurify/authentication'
import { logoutUrl } from '@procurify/authentication/src/configs'
import { SheetProvider, ModalProvider, ModalRoot } from '@procurify/ui'
import { theme } from '@procurify/ui-theme'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch, useHistory } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { ThemeWrapper } from '@webapp/app/providers/ThemeWrapper'
import { UIProvider } from '@webapp/app/providers/UIProvider'
import { SettingsRouter } from '@webapp/app/routers/SettingsRouter'
import { ConnectedAppHeader } from '@webapp/components/AppHeader/ConnectedAppHeader'
import { ErrorBoundary } from '@webapp/components/ErrorBoundary'
import { ConnectedGlobalAnalytics } from '@webapp/components/GlobalAnalytics'
import { GlobalErrorScreen } from '@webapp/components/GlobalErrorScreen'
import { Intercom } from '@webapp/components/Intercom'
import { getAppStatusMeta } from '@webapp/components/Navigation/utils'
import { SystemPushNotifications } from '@webapp/components/SystemPushNotifications'
import { PaginationProvider } from '@webapp/core/providers/Pagination/PaginationProvider'
import {
  makeSelectApplicationStatus,
  makeSelectIsAppReady,
  makeSelectRole,
} from '@webapp/core/stores/global'
import { ProcurifyDevToolsFfWarning } from '@webapp/dev/ProcurifyDevTools/ProcurifyDevToolsFfWarning'
import { ProcurifyDevToolsLoadable } from '@webapp/dev/ProcurifyDevTools/ProcurifyDevToolsLoadable'
import {
  useAppRoutes,
  useCheckNoPermission,
  useFeatureFlag,
} from '@webapp/hooks'
import { IS_DEV, IS_PROD } from '@webapp/infrastructure/envs'
import { AuthenticationErrorTypes } from '@webapp/models/enums/AuthenticationErrorType'
import { ExpenseRecordsRouter } from '@webapp/modules/accounts-payable/pages/ExpenseRecordsRouter'
import { ReconcileRouter } from '@webapp/modules/accounts-payable/pages/ReconcileRouter'
import { UnbilledItemPageRouter } from '@webapp/modules/accounts-payable/pages/Router'
import { ExpenseRouter, TravelsRouter } from '@webapp/modules/expense'
import { OrderRouter } from '@webapp/modules/purchasing'
import { NoPermissionsPage } from '@webapp/pages'
import { DiscoverBillPayPage } from '@webapp/pages/DiscoverBillPayPage'
import { PaymentFinancialAccountPage } from '@webapp/pages/PaymentFinancialAccountPage'
import { RedirectPage } from '@webapp/pages/RedirectPage'
import {
  GlobalCommandView,
  GlobalSearchProvider,
} from '@webapp/platform/global-command'
import { Permissions } from '@webapp/platform/iam'
import { AdminToolsPage } from '@webapp/platform/site-operations/pages/AdminToolsPage'
import { AppLoader } from './AppLoader'
import { AppContentWrapper } from './components/AppContentWrapper/AppContentWrapper'
import { GlobalStyles } from './components/GlobalStyles'
import { ToastContainerWrapper } from './components/ToastContainerWrapper/ToastContainerWrapper'
import { useModuleConfig } from './config/useModuleConfig'
import { usePersistReferrerUrl } from './hooks/usePersistReferrerUrl'
import { _DevRouter } from './routers/_DevRouter/_DevRouter'
import { BillRouter } from './routers/BillRouter/BillRouter'
import { ContractRouter } from './routers/ContractRouter'
import { DataAndInsightsRouter } from './routers/DataAndInsightsRouter'
import { DiscoverMoreRouter } from './routers/DiscoverMoreRouter'
import { InstanceManagementRouter } from './routers/InstanceManagementRouter'
import { IntegrationsRouter } from './routers/IntegrationsRouter'
import { PaymentRouter } from './routers/PaymentRouter'
import { ProcurementRouter } from './routers/ProcurementRouter'
import { PurchaseOrderRouter } from './routers/PurchaseOrderRouter'
import { ReceiveRouter } from './routers/ReceiveRouter'
import { ReportsRouter } from './routers/ReportsRouter'
import { SpendingCardRouter } from './routers/SpendingCardRouter'
import { UserProfileRouter } from './routers/UserProfileRouter'
import { VendorRouter } from './routers/VendorRouter'
import { AppStyled } from './styles/AppStyled'
import { AppContent } from '../components/AppContent'
import { ConnectedNavigation } from '../components/Navigation'
import { CatalogPage, LoginPage, WelcomePage } from '../pages'

const AuthenticatedApp = () => {
  usePersistReferrerUrl()

  const { brp, url } = useAppRoutes()

  const {
    routes: {
      enablePurchasingRoutes = false,
      enableEverythingButExpenseApRoutes = false,
    },
  } = useModuleConfig()

  // Check for trial mode and expired trial
  const applicationStatus = useSelector(makeSelectApplicationStatus())
  const role = useSelector(makeSelectRole())

  const CL_1324_NEW_NO_PERMISSION_PAGE = useFeatureFlag(
    'CL_1324_NEW_NO_PERMISSION_PAGE'
  )
  const AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION = useFeatureFlag(
    'AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION'
  )
  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')
  const DATA_1439_GLOBAL_SEARCH = useFeatureFlag('DATA_1439_GLOBAL_SEARCH')

  const hasNoPermission = useCheckNoPermission()

  const { isAdmin, isTrialMode, isTrialExpired } = getAppStatusMeta(
    applicationStatus,
    role
  )

  if (CL_1324_NEW_NO_PERMISSION_PAGE) {
    if (isTrialMode && isTrialExpired && !isAdmin && !hasNoPermission) {
      window.location.replace('/#/')
    }
  } else {
    if (isTrialMode && isTrialExpired && !isAdmin) {
      window.location.replace('/#/')
    }
  }

  return (
    <AppStyled>
      {DATA_1439_GLOBAL_SEARCH && !hasNoPermission ? (
        <GlobalSearchProvider>
          <ConnectedAppHeader showGlobalSearchTrigger />
          <GlobalCommandView />
        </GlobalSearchProvider>
      ) : (
        <ConnectedAppHeader />
      )}

      <AppContentWrapper>
        {!CL_1324_NEW_NO_PERMISSION_PAGE && <ConnectedNavigation />}

        {CL_1324_NEW_NO_PERMISSION_PAGE && !hasNoPermission && (
          <ConnectedNavigation />
        )}

        <ErrorBoundary>
          <SystemPushNotifications />
          <ConnectedGlobalAnalytics />
          <PaginationProvider>
            <ModalProvider>
              <SheetProvider>
                <ModalRoot />
                <AppContent>
                  {IS_DEV && <ProcurifyDevToolsFfWarning />}

                  <Switch>
                    {!PSERV_346_BASE_MIGRATION && (
                      <Route
                        exact
                        path='/'
                        render={() => {
                          window.location.reload()
                          return null
                        }}
                      />
                    )}
                    <Route
                      path='/spending-card'
                      component={SpendingCardRouter}
                    />
                    {enablePurchasingRoutes && (
                      <ProtectedRoute
                        path='/catalog'
                        component={CatalogPage}
                        navigationPermissions={['procure__product_catalog']}
                      />
                    )}
                    <Route path='/profile' component={UserProfileRouter} />
                    <Route path='/welcome' component={WelcomePage} />
                    <Route path='/settings' component={SettingsRouter} />
                    <Route path='/expenses' component={ExpenseRouter} />
                    {/* Travel request pages */}
                    {/* PSERV_346: flag-off falls back to the Angular travel pages. */}
                    {PSERV_346_BASE_MIGRATION && (
                      <Route path='/travels' component={TravelsRouter} />
                    )}
                    {!PSERV_346_BASE_MIGRATION && (
                      <Redirect
                        from={brp('TravelDetailsPage', {
                          uuid: ':uuid([0-9a-f]{32})',
                        })}
                        to='/#/travels/:uuid'
                      />
                    )}
                    {!PSERV_346_BASE_MIGRATION && (
                      <Redirect from='/travels' to='/#/travels/requests' />
                    )}
                    {/* Accounts Payable pages */}
                    {AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION && (
                      <Route
                        path='/unbilled'
                        component={UnbilledItemPageRouter}
                      />
                    )}
                    {/* Credit card reconciliation pages */}
                    {/* PSERV_346: flag-off falls back to the Angular reconcile page. */}
                    {PSERV_346_BASE_MIGRATION && (
                      <Route path='/ap/reconcile' component={ReconcileRouter} />
                    )}
                    {!PSERV_346_BASE_MIGRATION && (
                      <Redirect from='/ap/reconcile' to='/#/ap/reconcile' />
                    )}
                    {/* Legacy Payments expense records pages */}
                    {/* PSERV_346: flag-off falls back to the Angular expense-records page. */}
                    {PSERV_346_BASE_MIGRATION && (
                      <Route
                        path='/ap/expense-records'
                        component={ExpenseRecordsRouter}
                      />
                    )}
                    {!PSERV_346_BASE_MIGRATION && (
                      <Redirect
                        from='/ap/expense-records'
                        to='/#/ap/expense-records/unrecorded'
                      />
                    )}
                    {/* Bill pages */}
                    <Route path='/bills' component={BillRouter} />
                    {/* Contract pages */}
                    <Route path='/contracts' component={ContractRouter} />
                    {/* Financial account page */}
                    {enableEverythingButExpenseApRoutes && (
                      <ProtectedRoute
                        path={brp('FinancialAccountPage')}
                        component={PaymentFinancialAccountPage}
                        customFilter={({ featureSwitches, permissions }) => {
                          return (
                            (featureSwitches.bill_pay ||
                              featureSwitches.enable_airwallex_issuing) &&
                            permissions.includes(
                              Permissions.SETTINGS_MANAGE_FINANCIAL_ACCOUNT
                            )
                          )
                        }}
                      />
                    )}
                    {/* Bill Pay discovery promo page (front-end-only, no
                    Manage Financial Account permission gate) */}
                    <ProtectedRoute
                      path={brp('DiscoverBillPayPage')}
                      component={DiscoverBillPayPage}
                      featureFlags={['PAY_4248_BILL_PAY_NAVIGATION_PROMO']}
                    />
                    {/* Payment pages */}
                    <Route path='/payments' component={PaymentRouter} />
                    {/* Procurement */}
                    <Route path='/procurement' component={ProcurementRouter} />
                    {/* Data and Insights */}
                    <Route
                      path='/analytics'
                      component={DataAndInsightsRouter}
                    />
                    {/* Admin tools pages */}
                    {/* PSERV_346: flag-off falls back to the Angular admin tools page. */}
                    {PSERV_346_BASE_MIGRATION && (
                      <ProtectedRoute
                        path={brp('AdminToolsPage', {
                          tool: ':tool(feature-switch|trials|domain-switching|go-live|delete-request|reroute-approval|close-budgets|notifications|generate-data)',
                        })}
                        permissions={[Permissions.SITE_ADMINISTRATION]}
                        component={AdminToolsPage}
                      />
                    )}
                    {/* Default + unknown-tool redirect — must stay AFTER the constrained route above. */}
                    {PSERV_346_BASE_MIGRATION && (
                      <Redirect
                        from={brp('AdminToolsPage', { tool: ':tool?' })}
                        to={url('AdminToolsPage', { tool: 'feature-switch' })}
                      />
                    )}
                    {!PSERV_346_BASE_MIGRATION && (
                      <Redirect from='/admin-tools' to='/#/admin-tools' />
                    )}
                    {/* Order pages */}
                    <Route path='/orders' component={OrderRouter} />
                    {/* PO pages */}
                    <Route
                      path='/purchase-orders'
                      component={PurchaseOrderRouter}
                    />
                    {/* Receive pages */}
                    <Route path='/receive' component={ReceiveRouter} />
                    {/* Report pages */}
                    {/* PSERV_346: ReportsRouter self-gates each page — with the
                        flag off it redirects to the Angular reports. It must be
                        mounted unconditionally, otherwise those flag-off
                        redirects never render and /reports/* falls through to
                        the dashboard. */}
                    <Route path='/reports' component={ReportsRouter} />
                    {/* Integration pages */}
                    <Route
                      path='/integrations'
                      component={IntegrationsRouter}
                    />
                    {/* Instance management pages */}
                    <Route
                      path='/instance-management'
                      component={InstanceManagementRouter}
                    />
                    {/* Vendors pages */}
                    <Route path='/vendors' component={VendorRouter} />
                    <Route path='/vendor' component={VendorRouter} />

                    {/* Discover more add-on promotion page (superuser only) */}
                    <Route
                      path='/discover-more'
                      component={DiscoverMoreRouter}
                    />

                    <Route path='/redirect' component={RedirectPage} />
                    {IS_DEV && <Route path='/dev' component={_DevRouter} />}
                    {CL_1324_NEW_NO_PERMISSION_PAGE && hasNoPermission && (
                      <Route
                        path='/permissions-pending'
                        component={NoPermissionsPage}
                      />
                    )}
                    <Route path='*' render={() => <Redirect to='/' />} />
                  </Switch>
                </AppContent>
              </SheetProvider>
            </ModalProvider>
          </PaginationProvider>
          {IS_PROD && <Intercom />}
        </ErrorBoundary>
        <ToastContainerWrapper />
      </AppContentWrapper>
    </AppStyled>
  )
}

const PublicRoutes = () => {
  return [<Route key='login' path='/login' component={LoginPage} />]
}

export const App = () => {
  const history = useHistory()

  const {
    isLoading,
    isAuthenticated,
    error: authError,
    errorCode: authErrorCode,
  } = useAuthentication()
  const isAppReady = useSelector(makeSelectIsAppReady())

  const showAuthenticatedApp = isAuthenticated && isAppReady

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      history.push('/login')
    }
  }, [isLoading, isAuthenticated, history])

  useEffect(() => {
    if (authErrorCode === AuthenticationErrorTypes.STAFF_ACCESS_EXPIRED) {
      window.location.replace(logoutUrl)
    }
  }, [authErrorCode])

  const renderApplication = () => {
    if (isLoading) return <AppLoader />

    if (authError) return <GlobalErrorScreen />

    return (
      <Switch>
        {PublicRoutes()}

        {showAuthenticatedApp && (
          <Route path='*' component={AuthenticatedApp} />
        )}
      </Switch>
    )
  }

  return (
    <ThemeWrapper theme={theme}>
      <UIProvider>
        <GlobalStyles />

        {/* Mounted above AuthenticatedApp so the dev saga is listening before
            the boot UPDATE_FEATURE_FLAGS dispatch — persisted FF overrides must
            be merged into the store before flag-gated routes first render. */}
        {IS_DEV && <ProcurifyDevToolsLoadable />}

        {renderApplication()}
      </UIProvider>
    </ThemeWrapper>
  )
}
